import React, { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, AlertTriangle, Clock, BarChart3, CheckCircle2, FileText, ChevronRight, Sparkles } from 'lucide-react';
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { useTheme } from '../../../context/ThemeContext';
import { apiGet } from '../../../utils/api';
import AskJPro from '../AskJPro';
import toast from 'react-hot-toast';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

// Mirror-derived progress stages (from AuditProgress). Outreach states
// (contacted/reminded/unresponsive) live on the contact hub, not here.
const STATUS_COLORS = {
  no_cycle: '#9ca3af', not_started: '#f97316', collecting: '#3b82f6',
  in_review: '#8b5cf6', complete: '#10b981',
};

const STATUS_LABELS = {
  no_cycle: 'No Cycle', not_started: 'Not Started', collecting: 'Collecting',
  in_review: 'In Review', complete: 'Complete',
};

const FUNNEL_ORDER = ['no_cycle','not_started','collecting','in_review','complete'];

const CommandCenterTab = ({ auditId, auditYear, onPropertyClick, onFilterToStatus }) => {
  const { theme } = useTheme();
  const d = theme === 'dark';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAskJPro, setShowAskJPro] = useState(false);

  useEffect(() => {
    if (!auditId) return;
    const load = async () => {
      setLoading(true);
      try {
        const res = await apiGet(`/admin/audit_tracker/audits/${auditId}/analytics`);
        if (res.ok) setData(await res.json());
      } catch (err) { toast.error(err.message); }
      finally { setLoading(false); }
    };
    load();
  }, [auditId]);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" /></div>;
  if (!data) return null;

  const { kpis, status_funnel, needs_action, monthly_progress, team_workload } = data;

  const chartTooltipStyle = { background: d ? '#1f2937' : '#fff', border: 'none', borderRadius: 8, fontSize: 12, fontFamily: 'Rasa' };

  return (
    <div className="space-y-6">

      {/* ── Hero KPI Cards ── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Portfolio Progress */}
        <div className={`rounded-xl p-5 ${d ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'} shadow-sm`}>
          <div className={`text-[11px] font-bold uppercase tracking-widest font-rasa mb-3 ${d ? 'text-gray-400' : 'text-gray-500'}`}>Portfolio Progress</div>
          <div className="flex items-center gap-4">
            <div className="relative w-16 h-16">
              <svg className="w-16 h-16 -rotate-90" viewBox="0 0 36 36">
                <path d="M18 2.0845a 15.9155 15.9155 0 0 1 0 31.831 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke={d ? '#374151' : '#e5e7eb'} strokeWidth="3" />
                <path d="M18 2.0845a 15.9155 15.9155 0 0 1 0 31.831 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke="#10b981" strokeWidth="3" strokeDasharray={`${kpis.completion_rate}, 100`} strokeLinecap="round" />
              </svg>
              <span className="absolute inset-0 flex items-center justify-center text-sm font-dm-serif font-bold">{kpis.completion_rate}%</span>
            </div>
            <div>
              <div className="text-xl font-dm-serif">{kpis.completed}/{kpis.total}</div>
              <div className={`text-[12px] font-rasa ${d ? 'text-gray-400' : 'text-gray-500'}`}>properties complete</div>
            </div>
          </div>
        </div>

        {/* Collection Rate */}
        <div className={`rounded-xl p-5 ${d ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'} shadow-sm`}>
          <div className={`text-[11px] font-bold uppercase tracking-widest font-rasa mb-3 ${d ? 'text-gray-400' : 'text-gray-500'}`}>Collection Rate</div>
          <div className="text-2xl font-dm-serif mb-2">{kpis.collection_rate}%</div>
          <div className={`w-full h-2 rounded-full ${d ? 'bg-gray-700' : 'bg-gray-200'}`}>
            <div className="h-2 rounded-full bg-blue-500 transition-all" style={{ width: `${kpis.collection_rate}%` }} />
          </div>
          <div className={`text-[12px] font-rasa mt-1.5 ${d ? 'text-gray-400' : 'text-gray-500'}`}>{kpis.total_received} of {kpis.total_required} files received</div>
        </div>

        {/* Attention Needed */}
        <button
          onClick={() => onFilterToStatus && onFilterToStatus('attention')}
          className={`text-left rounded-xl p-5 transition-all ${
            kpis.attention_needed > 0
              ? (d ? 'bg-red-900/20 border border-red-800/50 hover:border-red-700' : 'bg-red-50 border border-red-200 hover:border-red-300')
              : (d ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200')
          } shadow-sm`}
        >
          <div className="flex items-center justify-between mb-3">
            <span className={`text-[11px] font-bold uppercase tracking-widest font-rasa ${d ? 'text-gray-400' : 'text-gray-500'}`}>Attention Needed</span>
            {kpis.attention_needed > 0 && <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />}
          </div>
          <div className="text-3xl font-dm-serif">{kpis.attention_needed}</div>
          <div className={`text-[12px] font-rasa mt-1 ${d ? 'text-gray-400' : 'text-gray-500'}`}>properties need action</div>
        </button>

        {/* Avg Days to Complete */}
        <div className={`rounded-xl p-5 ${d ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'} shadow-sm`}>
          <div className={`text-[11px] font-bold uppercase tracking-widest font-rasa mb-3 ${d ? 'text-gray-400' : 'text-gray-500'}`}>Avg Days to Complete</div>
          <div className="flex items-baseline gap-2">
            <span className="text-3xl font-dm-serif">{kpis.avg_completion_days || '-'}</span>
            <span className={`text-sm font-rasa ${d ? 'text-gray-400' : 'text-gray-500'}`}>days</span>
          </div>
          <div className={`flex items-center gap-1 mt-1 text-[12px] font-rasa ${d ? 'text-gray-400' : 'text-gray-500'}`}>
            <Clock size={12} /> Corrections rate: {kpis.corrections_rate}%
          </div>
        </div>
      </div>

      {/* ── Status Funnel (full width, compact) ── */}
      <div className={`rounded-xl px-5 py-4 ${d ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'} shadow-sm`}>
        <div className="flex items-center justify-between mb-3">
          <div className={`text-[11px] font-bold uppercase tracking-widest font-rasa ${d ? 'text-gray-400' : 'text-gray-500'}`}>Audit Pipeline</div>
          <div className="flex flex-wrap gap-x-3 gap-y-1">
            {FUNNEL_ORDER.map((s) => {
              const count = status_funnel[s] || 0;
              if (count === 0) return null;
              return (
                <button key={s} onClick={() => onFilterToStatus && onFilterToStatus(s)} className="flex items-center gap-1 hover:opacity-70">
                  <div className="w-2 h-2 rounded-sm" style={{ backgroundColor: STATUS_COLORS[s] }} />
                  <span className={`text-[11px] font-rasa ${d ? 'text-gray-400' : 'text-gray-500'}`}>{STATUS_LABELS[s]} ({count})</span>
                </button>
              );
            })}
          </div>
        </div>
        <div className="flex h-8 rounded-lg overflow-hidden gap-0.5">
          {FUNNEL_ORDER.map((s) => {
            const count = status_funnel[s] || 0;
            if (count === 0) return null;
            const pct = kpis.total > 0 ? (count / kpis.total * 100) : 0;
            return (
              <button
                key={s}
                onClick={() => onFilterToStatus && onFilterToStatus(s)}
                className="relative group transition-opacity hover:opacity-80"
                style={{ width: `${Math.max(pct, 2.5)}%`, backgroundColor: STATUS_COLORS[s] }}
                title={`${STATUS_LABELS[s]}: ${count}`}
              >
                {pct > 6 && <span className="absolute inset-0 flex items-center justify-center text-white text-[11px] font-bold font-rasa">{count}</span>}
                <div className="absolute bottom-full mb-1.5 left-1/2 -translate-x-1/2 hidden group-hover:block z-10">
                  <div className={`text-[11px] px-2.5 py-1 rounded-lg whitespace-nowrap font-rasa font-bold shadow-lg ${d ? 'bg-gray-700 text-gray-100' : 'bg-gray-800 text-white'}`}>
                    {STATUS_LABELS[s]}: {count} ({pct.toFixed(0)}%)
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Needs Action + Charts in 2-col grid ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

      {/* Needs Action */}
      <div className={`rounded-xl p-5 ${d ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'} shadow-sm`}>
          <div className={`text-[11px] font-bold uppercase tracking-widest font-rasa mb-3 ${d ? 'text-gray-400' : 'text-gray-500'}`}>
            Needs Action ({needs_action?.length || 0})
          </div>
          {(!needs_action || needs_action.length === 0) ? (
            <div className="flex flex-col items-center justify-center py-8">
              <CheckCircle2 size={28} className="text-emerald-500 mb-2" />
              <p className={`text-sm font-rasa ${d ? 'text-gray-400' : 'text-gray-500'}`}>All caught up</p>
            </div>
          ) : (
            <div className="space-y-1 max-h-[320px] overflow-y-auto pr-1">
              {needs_action.map((item) => (
                <button
                  key={item.id}
                  onClick={() => onPropertyClick && onPropertyClick(item.id)}
                  className={`w-full text-left p-3 rounded-lg transition-colors ${d ? 'hover:bg-gray-700/50' : 'hover:bg-gray-50'}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className={`text-[13px] font-medium font-rasa truncate ${d ? 'text-gray-200' : 'text-gray-700'}`}>
                        {item.property_name}
                        {item.urgency > 50 && <span className="ml-1.5 w-1.5 h-1.5 rounded-full bg-red-500 inline-block animate-pulse" />}
                      </div>
                      <div className={`text-[11px] font-rasa mt-0.5 ${d ? 'text-gray-500' : 'text-gray-400'}`}>
                        {item.reasons?.slice(0, 2).join(' · ')}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {item.days_in_status > 0 && (
                        <span className={`text-[11px] font-rasa font-bold ${
                          item.days_in_status > 21 ? 'text-red-500' : item.days_in_status > 14 ? 'text-orange-500' : (d ? 'text-gray-400' : 'text-gray-500')
                        }`}>{item.days_in_status}d</span>
                      )}
                      <ChevronRight size={14} className={d ? 'text-gray-600' : 'text-gray-300'} />
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
      </div>

        {/* Monthly Progress */}
        <div className={`rounded-xl p-5 ${d ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'} shadow-sm`}>
          <div className={`text-[11px] font-bold uppercase tracking-widest font-rasa mb-4 ${d ? 'text-gray-400' : 'text-gray-500'}`}>Monthly Progress</div>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={(monthly_progress || []).map((m) => ({ ...m, name: MONTHS[m.month - 1] }))} margin={{ top: 5, right: 5, bottom: 0, left: -20 }}>
              <defs>
                <linearGradient id="completedGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="reviewGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.2} />
                  <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: d ? '#6b7280' : '#9ca3af', fontFamily: 'Rasa' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: d ? '#6b7280' : '#9ca3af', fontFamily: 'Rasa' }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={chartTooltipStyle} />
              <Area type="monotone" dataKey="expected" stroke="#94a3b8" strokeDasharray="4 4" fill="none" strokeWidth={1.5} name="Expected" />
              <Area type="monotone" dataKey="in_review" stroke="#8b5cf6" fill="url(#reviewGrad)" strokeWidth={1.5} name="In Review" />
              <Area type="monotone" dataKey="completed" stroke="#10b981" fill="url(#completedGrad)" strokeWidth={2} name="Completed" />
            </AreaChart>
          </ResponsiveContainer>
          <div className="flex items-center gap-5 mt-2">
            {[{ color: '#10b981', label: 'Completed' }, { color: '#8b5cf6', label: 'In Review' }, { color: '#94a3b8', label: 'Expected', dashed: true }].map((l) => (
              <div key={l.label} className="flex items-center gap-1.5">
                <div className="w-4 h-0.5" style={{ backgroundColor: l.color, borderTop: l.dashed ? '2px dashed' : 'none', borderColor: l.color }} />
                <span className={`text-[11px] font-rasa ${d ? 'text-gray-400' : 'text-gray-500'}`}>{l.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Team Workload (full row) */}
        <div className={`lg:col-span-2 rounded-xl p-5 ${d ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'} shadow-sm`}>
          <div className={`text-[11px] font-bold uppercase tracking-widest font-rasa mb-4 ${d ? 'text-gray-400' : 'text-gray-500'}`}>Team Workload</div>
          {team_workload && team_workload.length > 1 ? (
            <ResponsiveContainer width="100%" height={Math.max(120, team_workload.length * 40)}>
              <BarChart data={team_workload} layout="vertical" margin={{ top: 0, right: 10, bottom: 0, left: 0 }}>
                <XAxis type="number" tick={{ fontSize: 11, fill: d ? '#6b7280' : '#9ca3af', fontFamily: 'Rasa' }} axisLine={false} tickLine={false} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 12, fill: d ? '#9ca3af' : '#6b7280', fontFamily: 'Rasa' }} axisLine={false} tickLine={false} width={100} />
                <Tooltip contentStyle={chartTooltipStyle} />
                <Bar dataKey="collection" stackId="a" fill="#f97316" name="Collection" radius={[0, 0, 0, 0]} />
                <Bar dataKey="review" stackId="a" fill="#8b5cf6" name="Review" />
                <Bar dataKey="completed" stackId="a" fill="#10b981" name="Completed" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : team_workload?.length === 1 && team_workload[0].name === 'Unassigned' ? (
            <div className={`text-center py-6`}>
              <div className={`text-3xl font-dm-serif mb-1 ${d ? 'text-gray-300' : 'text-gray-700'}`}>{team_workload[0].total}</div>
              <div className={`text-[13px] font-rasa ${d ? 'text-gray-400' : 'text-gray-500'}`}>properties unassigned</div>
              <div className={`text-[12px] font-rasa mt-2 ${d ? 'text-gray-500' : 'text-gray-400'}`}>
                Assign team members in the Properties tab to see workload distribution
              </div>
            </div>
          ) : (
            <div className={`text-center py-6 text-[13px] font-rasa ${d ? 'text-gray-500' : 'text-gray-400'}`}>No properties in this audit</div>
          )}
          <div className="flex items-center gap-5 mt-2">
            {[{ color: '#f97316', label: 'Collection' }, { color: '#8b5cf6', label: 'Review' }, { color: '#10b981', label: 'Completed' }].map((l) => (
              <div key={l.label} className="flex items-center gap-1.5">
                <div className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: l.color }} />
                <span className={`text-[11px] font-rasa ${d ? 'text-gray-400' : 'text-gray-500'}`}>{l.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Ask JPro Button ── */}
      <div className="flex justify-end">
        <button
          onClick={() => setShowAskJPro(true)}
          className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-[13px] font-rasa font-medium bg-gradient-to-r from-blue-600 to-violet-600 text-white hover:from-blue-700 hover:to-violet-700 shadow-sm"
        >
          <Sparkles size={14} /> Ask JPro
        </button>
      </div>

      {/* ── Ask JPro Modal ── */}
      {showAskJPro && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-20 px-4 bg-black/40" onClick={() => setShowAskJPro(false)}>
          <div className="w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
            <AskJPro
              portfolioData={{ ...kpis, action_items: needs_action, team_workload }}
              mode="portfolio"
              audience="internal"
              onClose={() => setShowAskJPro(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default CommandCenterTab;
